import React, { useEffect, useRef, useState } from "react";
import { fetchClientInfo, castVote } from "./api";
import { ClientInfo } from "./types";
import Tile from "./Tile";

const VotingBoard: React.FC = () => {
  const [serverData, setServerData] = useState<ClientInfo | null>(null);
  const [pendingVotes, setPendingVotes] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFatalError, setIsFatalError] = useState(false);

  const pendingVotesRef = useRef<number[]>([]);
  const isRequestInProgress = useRef(false);

  useEffect(() => {
    fetchClientInfo()
      .then((data) => {
        setServerData(data);
        pendingVotesRef.current = new Array(data.vote_data.length).fill(0);
        setPendingVotes([...pendingVotesRef.current]);
        setIsLoading(false);
      })
      .catch(() => {
        setIsFatalError(true);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (isLoading || isFatalError || isRequestInProgress.current || pendingVotesRef.current.length === 0) return;

      const currentPending = [...pendingVotesRef.current];
      const hasPending = currentPending.some((v) => v > 0);

      isRequestInProgress.current = true;

      try {
        let newData: ClientInfo;

        if (hasPending) {
          newData = await castVote(currentPending);
          
          // Ha sikeres a küldés, kivonjuk az elküldött mennyiséget
          pendingVotesRef.current = pendingVotesRef.current.map(
            (v, i) => Math.max(0, v - currentPending[i])
          );
        } else {
          newData = await fetchClientInfo();
        }

        // Ha a szerver szerint épp zárolva van a szavazás (kör vége), azonnal eldobjuk az esetlegesen nálunk ragadt extra kattintásokat
        if (!newData.can_vote) {
          pendingVotesRef.current = new Array(newData.vote_data.length).fill(0);
        }

        setPendingVotes([...pendingVotesRef.current]);
        setServerData(newData);
      } catch (error: any) {
        // Ha valamiért mégis elutasít a szerver (pl. pont lecsúsztunk a körről, vagy hálózati hiba), nullázzuk a pendinget
        if (error.response) {
          pendingVotesRef.current = new Array(pendingVotesRef.current.length).fill(0);
          setPendingVotes([...pendingVotesRef.current]);
        }
      } finally {
        isRequestInProgress.current = false;
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoading, isFatalError]);

  const handleSelect = (index: number) => {
    // Csak akkor engedünk kattintani, ha a szerver szerint lehet szavazni
    if (!serverData || serverData.over || !serverData.can_vote) return;
    
    pendingVotesRef.current[index] += 1;
    setPendingVotes([...pendingVotesRef.current]);
  };

  if (isLoading) return <Message text="Loading..." />;
  if (isFatalError || !serverData) return <Message text="Can't reach the server." />;

  const { over, vote_data, can_vote } = serverData;
  const voteBlocked = !can_vote;
  const displayData = vote_data.map((v, i) => v + (pendingVotes[i] || 0));

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-6 p-2">
      <h1 className="text-white text-xl font-semibold text-center">
        {over 
          ? "Game over" 
          : voteBlocked 
            ? "Waiting for the next round" 
            : "Tap the columns to charge!"}
      </h1>

      <div
        className={`grid gap-1.5 w-full max-w-3xl px-1 transition-opacity duration-300 ${voteBlocked ? 'opacity-40 grayscale' : 'opacity-100'}`}
        style={{ gridTemplateColumns: `repeat(${vote_data.length}, minmax(0, 1fr))` }}
      >
        {displayData.map((votes, index) => (
          <Tile
            key={index}
            index={index}
            votes={votes}
            selected={pendingVotes[index] > 0} 
            disabled={over || voteBlocked} // Itt adjuk át a letiltást a gomboknak
            onSelect={handleSelect}
          />
        ))}
      </div>

      {voteBlocked && (
        <p className="text-red-400 text-sm mt-2 text-center">
          Voting is closed right now, try again next round.
        </p>
      )}
    </div>
  );
};

const Message: React.FC<{ text: string }> = ({ text }) => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <p className="text-white text-lg">{text}</p>
  </div>
);

export default VotingBoard;